import React, { useState, useEffect } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Grid,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CssBaseline from "@mui/material/CssBaseline";
import dayjs from "dayjs";
import {
  getDayWiseBatchStatusCount,
  getProngOvenDuration,
  getDippingTankList,
  getDippingTankCellValues,
  getLeachingConductivity,
  getAlarmTolaranceDetail,
} from "../../containers/Tunnel Oven/services";

const theme = createTheme({
  palette: {
    background: {
      default: "#f4f6f8",
    },
    text: {
      primary: "#333",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h5: {
      fontWeight: 600,
      color: "#2a3f54",
    },
    h6: {
      fontWeight: 600,
      color: "#2a3f54",
      fontSize: "1rem",
      textAlign: "center",
      marginBottom: "1rem",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
          border: "1px solid #e0e0e0",
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: "1px solid #e0e0e0",
          textAlign: "center",
          padding: "12px",
        },
        head: {
          fontWeight: "bold",
          backgroundColor: "#f8f9fa",
        },
      },
    },
  },
});

const CustomTable = ({ title, headers, data, singleRow = false }) => (
  <Card sx={{ height: "100%" }}>
    {/* Title Rendering Section */}
    {typeof title === "string" ? (
      <Typography
        variant="h6"
        className="mb-0 m-1"
        sx={{ textAlign: "center" }}
      >
        {title}
      </Typography>
    ) : (
      // If title is an object (for Dipping and Prong Oven with dropdown)
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          margin: "4px",
        }}
      >
        {title.text ? (
          // Case: title has both text and a right-aligned element (e.g., Dipping)
          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            <Typography variant="h6" sx={{ marginBottom: 0 }}>
              {title.text}
            </Typography>
          </Box>
        ) : (
          // Case: title is primarily the right-aligned element (e.g., Prong Oven dropdown)
          // This will make the dropdown itself appear centered if it's the only main element
          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            {title.rightElement}
          </Box>
        )}
        {/* Render rightElement only if title.text exists (as it's already rendered above if not) */}
        {title.text && title.rightElement}
      </Box>
    )}

    {/* Table Content Section (Always rendered) */}
    <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
      <Table size="small" className="process-parameter-table">
        <TableHead>
          <TableRow>
            {headers.map((header) => (
              <TableCell key={header}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {singleRow ? (
            <TableRow>
              {data.map((item, index) => (
                <TableCell
                  key={index}
                  sx={{
                    backgroundColor: item.highlight
                      ? "red !important"
                      : "inherit",
                    color: item.highlight ? "white !important" : "inherit",
                  }}
                >
                  {item.value}
                </TableCell>
              ))}
            </TableRow>
          ) : (
            data.map((row, index) => (
              <TableRow key={index}>
                <TableCell>{row.label}</TableCell>
                <TableCell
                  sx={{
                    backgroundColor: row.highlight
                      ? "red !important"
                      : "inherit",
                    color: row.highlight ? "white !important" : "inherit",
                  }}
                >
                  {row.value}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Card>
);

const ProcessParametersComponent = ({ mergedArray }) => {
  const [selectedProngOven, setSelectedProngOven] = useState("Prong Oven 1");
  const [prongOvenTime, setProngOvenTime] = useState(0);
  const [dippingOptions, setDippingOptions] = useState([]);
  const [selectedDippingOption, setSelectedDippingOption] = useState("");
  const [dippingData, setDippingData] = useState([
    { label: "Latex Temp (°C)", value: 0, highlight: false },
    { label: "Latex PH", value: 0, highlight: false },
    { label: "Latex Viscosity (Sec)", value: 0, highlight: false },
    { label: "Latex Maturation (mm)", value: 0, highlight: false },
    { label: "Latex Plate Test", value: 0, highlight: false },
    { label: "Temperature (°C)", value: 0, highlight: false },
    { label: "Specific Gravity", value: 0, highlight: false },
    { label: "Foam Test (mm)", value: 0, highlight: false },
  ]);
  const [leachingTank1Conductivity, setLeachingTank1Conductivity] = useState(0);
  const [leachingTank2Conductivity, setLeachingTank2Conductivity] = useState(0);
  const [alarmToleranceData, setAlarmToleranceData] = useState({});

  const getProngOvenTagKey = (ovenName) => {
    return ovenName === "Big Oven"
      ? "BIG_ACT_TEMP"
      : `PRO_${ovenName.split(" ")[2]}_ACT_TEMP`;
  };
  const getProngOvenSetTagKey = (ovenName) => {
    if (ovenName === "Big Oven") return "BIG_SET_TEMP";
    const ovenNumber = ovenName.split(" ")[2];
    return `PRO_${ovenNumber}_SET_TEMP`;
  };
  useEffect(() => {
    const fetchProngOvenDuration = async () => {
      try {
        const response = await getProngOvenDuration(selectedProngOven);
        setProngOvenTime(response.data.payload?.total_duration_minutes || 0);
      } catch (error) {
        console.error("Error fetching prong oven duration:", error);
        setProngOvenTime(0);
      }
    };

    fetchProngOvenDuration();
  }, [selectedProngOven]);

  // Effect to fetch dipping tank list and initially set selected dipping option
  useEffect(() => {
    const fetchDippingTankList = async () => {
      try {
        const response = await getDippingTankList();
        if (response.data.payload && response.data.payload.length > 0) {
          setDippingOptions(response.data.payload);
          // Do NOT set default here. Let the new effect handle default selection.
        }
      } catch (error) {
        console.error("Error fetching dipping tank list:", error);
      }
    };

    fetchDippingTankList();
  }, []);

  // New Effect to fetch dipping tank values and auto-select highlighted tank
  useEffect(() => {
    let intervalId;

    const fetchDippingTankCellValuesAndHighlight = async (
      dippingTankName = selectedDippingOption
    ) => {
      if (!dippingTankName && dippingOptions.length > 0) {
        dippingTankName = dippingOptions[0]; // Fallback to first if not explicitly passed
      }

      if (dippingTankName) {
        try {
          const response = await getDippingTankCellValues(dippingTankName);
          const payload = response.data.payload;
          if (payload) {
            const currentDippingData = [
              {
                label: "Latex Temp (°C)",
                value: payload.latex_temp || 0,
                highlight: payload.is_latex_temp_alarm === true,
              },
              {
                label: "Latex PH",
                value: payload.latex_ph || 0,
                highlight: payload.is_latex_ph_alarm === true,
              },
              {
                label: "Latex Viscosity (Sec)",
                value: payload.latex_visosity || 0,
                highlight: payload.is_latex_visosity_alarm === true,
              },
              {
                label: "Latex Maturation (mm)",
                value: payload.latex_maturation || 0,
                highlight: payload.is_latex_maturation_alarm === true,
              },
              {
                label: "Latex Plate Test",
                value: payload.latex_plate_test || 0,
                highlight: payload.is_latex_plate_test_alarm === true,
              },
              {
                label: "Temperature (°C)",
                value: payload.temprature || "-",
                highlight: payload.is_temperature_alarm === true,
              },
              {
                label: "Specific Gravity",
                value: payload.specific_gravity || "-",
                highlight: payload.is_specific_gravity_alarm === true,
              },
              {
                label: "Foam Test (mm)",
                value: payload.foam_test || "-",
                highlight: payload.is_foam_test_alarm === true,
              },
            ];
            setDippingData(currentDippingData);

            // Check if any value in the current dipping tank is highlighted
            const isAnyValueHighlighted = currentDippingData.some(
              (item) => item.highlight
            );

            return {
              tankName: dippingTankName,
              isHighlighted: isAnyValueHighlighted,
            };
          }
        } catch (error) {
          console.error(
            `Error fetching dipping tank cell values for ${dippingTankName}:`,
            error
          );
        }
      }
      return { tankName: dippingTankName, isHighlighted: false };
    };

    // Auto-select a highlighted dipping tank
    const autoSelectDippingTank = async () => {
      if (dippingOptions.length > 0) {
        let selectedDefault = false;
        for (const option of dippingOptions) {
          // Temporarily fetch data for each option to check highlight status
          // This might involve multiple API calls initially, which is acceptable
          // for setting the default on load.
          const { isHighlighted } =
            await fetchDippingTankCellValuesAndHighlight(option);
          if (isHighlighted) {
            setSelectedDippingOption(option);
            selectedDefault = true;
            break; // Select the first one found
          }
        }
        if (!selectedDefault && selectedDippingOption === "") {
          setSelectedDippingOption(dippingOptions[0]); // Fallback to first option if none highlighted
        }
      }
    };

    // Initial setup
    if (dippingOptions.length > 0 && selectedDippingOption === "") {
      autoSelectDippingTank();
    } else if (selectedDippingOption) {
      // If an option is already selected (either by user or initial auto-select),
      // fetch its values and set up polling.
      fetchDippingTankCellValuesAndHighlight();
      intervalId = setInterval(fetchDippingTankCellValuesAndHighlight, 5000);
    }

    // Cleanup on unmount or when selectedDippingOption changes
    return () => {
      clearInterval(intervalId);
    };
  }, [dippingOptions, selectedDippingOption]); // Add dippingOptions as dependency

  useEffect(() => {
    const fetchLeachingConductivity = async () => {
      try {
        const response = await getLeachingConductivity();
        if (response.data.payload) {
          setLeachingTank1Conductivity(
            response.data.payload.tank1_hot_conductivity || 0
          );
          setLeachingTank2Conductivity(
            response.data.payload.payload.tank2_cold_conductivity || 0
          );
        }
      } catch (error) {
        console.error("Error fetching leaching conductivity:", error);
      }
    };
    fetchLeachingConductivity();
  }, []);

  useEffect(() => {
    const fetchAlarmToleranceDetail = async () => {
      try {
        const response = await getAlarmTolaranceDetail();
        if (response.data.payload) {
          const allToleranceData = [
            ...(response.data.payload.prong_oven || []),
            ...(response.data.payload.tunnel_oven || []),
            ...(response.data.payload.leaching || []),
          ];

          const toleranceMap = allToleranceData.reduce((acc, item) => {
            acc[item.machine_tag] = {
              set_temp: parseFloat(item.set_temp),
              tolerance: parseFloat(item.tolerance),
            };
            return acc;
          }, {});
          setAlarmToleranceData(toleranceMap);
        }
      } catch (error) {
        console.error("Error fetching alarm tolerance detail:", error);
      }
    };
    fetchAlarmToleranceDetail();
  }, []);

  // Modified useEffect to set default selectedProngOven (only on initial load if not already set)
  useEffect(() => {
    // Only attempt auto-selection if no prong oven is currently selected
    // and we have the necessary data
    if (selectedProngOven === "Prong Oven 1" && mergedArray.length > 0 && Object.keys(alarmToleranceData).length > 0) {
      const allProngOvens = [
        ...Array.from({ length: 8 }, (_, i) => `Prong Oven ${i + 1}`),
        "Big Oven",
      ];

      let highlightedOvenFound = false;
      for (const ovenName of allProngOvens) {
        const tagKey = getProngOvenTagKey(ovenName);
        const prongOvenValue =
          mergedArray.find((e) => e.device_tag === tagKey)?.tagValue ?? "-";

        const prongOvenTolerance = alarmToleranceData[tagKey];
        const isHighlighted =
          prongOvenTolerance &&
          prongOvenValue !== undefined &&
          prongOvenValue !== null &&
          prongOvenValue !== ""
            ? parseFloat(prongOvenValue) >
                prongOvenTolerance.set_temp + prongOvenTolerance.tolerance ||
              parseFloat(prongOvenValue) <
                prongOvenTolerance.set_temp - prongOvenTolerance.tolerance
            : false;

        if (isHighlighted) {
          setSelectedProngOven(ovenName);
          highlightedOvenFound = true;
          break; // Select the first one found
        }
      }

      // No need for a fallback here, as "Prong Oven 1" is the initial state.
      // If no highlighted oven is found, it will remain "Prong Oven 1" until user changes.
    }
  }, [mergedArray, alarmToleranceData]); // Dependencies remain the same, but behavior is now conditional

  const handleDippingChange = (event) => {
    setSelectedDippingOption(event.target.value);
  };

  const prongOvenTagKey = getProngOvenTagKey(selectedProngOven);
  const prongOvenSetTagKey = getProngOvenSetTagKey(selectedProngOven);

  const prongOvenValue =
    mergedArray.find((e) => e.device_tag === prongOvenTagKey)?.tagValue ?? "-";

    const prongOvenSetValue =
  mergedArray.find((e) => e.device_tag === prongOvenSetTagKey)?.tagValue ?? "-";
  const prongOvenTolerance = alarmToleranceData[prongOvenTagKey];
  const isProngOvenValueHighlighted =
    prongOvenTolerance &&
    prongOvenValue !== undefined &&
    prongOvenValue !== null &&
    prongOvenValue !== ""
      ? parseFloat(prongOvenValue) >
          prongOvenTolerance.set_temp + prongOvenTolerance.tolerance ||
        parseFloat(prongOvenValue) <
          prongOvenTolerance.set_temp - prongOvenTolerance.tolerance
      : false;
  const prungOvenData = [
    { value: prongOvenValue, highlight: isProngOvenValueHighlighted },
    { value: prongOvenTime, highlight: false },
  ];

  const handleProngOvenChange = (event) => {
    setSelectedProngOven(event.target.value);
  };

  const tunnelOvenData = Array.from({ length: 8 }, (_, index) => {
    const zoneNumber = index + 1;
    const tagKey = `TUNL_OVN_Z${zoneNumber}_ACT_TEMP`;
    const tagKey1 = `TUNL_OVN_Z${zoneNumber}_SET_TEMP`;
  
    const actualValue =
      mergedArray.find((e) => e.device_tag === tagKey)?.tagValue ?? "-";
    const setValue =
      mergedArray.find((e) => e.device_tag === tagKey1)?.tagValue ?? "-";
  
    const toleranceInfo = alarmToleranceData[tagKey];
    const isHighlighted =
      toleranceInfo &&
      actualValue !== undefined &&
      actualValue !== null &&
      actualValue !== "" &&
      !isNaN(parseFloat(actualValue))
        ? parseFloat(actualValue) >
            toleranceInfo.set_temp + toleranceInfo.tolerance ||
          parseFloat(actualValue) <
            toleranceInfo.set_temp - toleranceInfo.tolerance
        : false;
  
    return {
      zone: `Z${zoneNumber} (${setValue})`, // ✅ fixed template string
      value: actualValue,
      highlight: isHighlighted,
    };
  });
  

  const leachingTank1Data = [
    {
      label: "Temperature (°C)",
      value:
        mergedArray.find((e) => e.device_tag === "HOT_LECH_T_ACT_TEMP")
          ?.tagValue ?? "-",
      highlight: (() => {
        const tagKey = "HOT_LECH_T_ACT_TEMP";
        const actualValue =
          mergedArray.find((e) => e.device_tag === tagKey)?.tagValue || "";
        const toleranceInfo = alarmToleranceData[tagKey];
        return toleranceInfo &&
          actualValue !== undefined &&
          actualValue !== null &&
          actualValue !== ""
          ? parseFloat(actualValue) >
              toleranceInfo.set_temp + toleranceInfo.tolerance ||
              parseFloat(actualValue) <
                toleranceInfo.set_temp - toleranceInfo.tolerance
          : false;
      })(),
    },
    {
      label: "Conductivity (us/cm)",
      value: leachingTank1Conductivity,
    },
    {
      label: "Water Level (mm)",
      value:
        mergedArray.find((e) => e.device_tag === "HOT_LECH_T_WATER_LEVAL")
          ?.tagValue ?? "-",
    },
  ];

  const leachingTank2Data = [
    {
      label: "Conductivity (us/cm)",
      value: leachingTank2Conductivity,
    },
    {
      label: "Water Level (mm)",
      value:
        mergedArray.find((e) => e.device_tag === "COLD_LECH_T_WATER_LEVAL")
          ?.tagValue ?? "-",
    },
  ];

  return (
    <Grid container spacing={2}>
      {/* Tunnel Oven */}
      <Grid item xs={12} md={8}>
        <Card>
          <Typography
            variant="h6"
            className="mb-0 m-1"
            sx={{ textAlign: "center" }}
          >
            Tunnel Oven - Temperature (°C)
          </Typography>
          <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
            <Table size="small" className="process-parameter-table">
              <TableHead>
                <TableRow>
                  {tunnelOvenData.map((item) => (
                    <TableCell key={item.zone}>{item.zone}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  {tunnelOvenData.map((item) => (
                    <TableCell
                      key={item.zone}
                      sx={{
                        backgroundColor: item.highlight
                          ? "red !important"
                          : "inherit",
                        color: item.highlight ? "white !important" : "inherit",
                      }}
                    >
                      {item.value}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </Grid>

      {/* Prong Oven */}
      <Grid item xs={12} md={4}>
        <CustomTable
          title={{
            rightElement: (
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minWidth: 120,
                }}
              >
                <select
                  id="prong-oven-select"
                  value={selectedProngOven}
                  onChange={handleProngOvenChange}
                  style={{
                    width: "auto",
                    padding: "8px",
                    borderRadius: "4px",
                    border: "none",
                    fontSize: "15px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                    textAlignLast: "center",
                  }}
                >
                  {Array.from({ length: 8 }, (_, i) => (
                    <option
                      key={`Prong Oven ${i + 1}`}
                      value={`Prong Oven ${i + 1}`}
                    >
                      {`Prong Oven ${i + 1}`}
                    </option>
                  ))}
                  <option value="Big Oven">Big Oven</option>
                </select>
              </Box>
            ),
          }}
          headers={[`Temperature (°C) (${prongOvenSetValue})`, "Oven Time(Hrs)"]}
          data={prungOvenData}
          singleRow={true}
        />
      </Grid>
      {/* Leaching Tank 1 */}
      <Grid item xs={12} md={6} style={{ paddingTop: "9px" }}>
        <CustomTable
          title="Leaching Tank 1"
          headers={[
            "Temperature (°C)",
            "Conductivity (us/cm)",
            "Water Level (mm)",
          ]}
          data={leachingTank1Data}
          singleRow
        />
      </Grid>

      {/* Leaching Tank 2 */}
      <Grid item xs={12} md={6} style={{ paddingTop: "9px" }}>
        <CustomTable
          title="Leaching Tank 2"
          headers={["Conductivity (us/cm)", "Water Level (mm)"]}
          data={leachingTank2Data}
          singleRow
        />
      </Grid>

      {/* Dipping */}
      <Grid item xs={12} style={{ paddingTop: "9px" }}>
        <CustomTable
          title={{
            text: "Dipping",
            rightElement: (
              <Box sx={{ minWidth: 120 }}>
                <select
                  id="dipping-select"
                  value={selectedDippingOption}
                  onChange={handleDippingChange}
                  style={{
                    padding: "0px",
                    borderRadius: "4px",
                    border: "1px solid #e0e0e0",
                    fontSize: "15px",
                    backgroundColor: "#fff",
                    cursor: "pointer",
                    fontWeight: 600,
                  }}
                >
                  {dippingOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </Box>
            ),
          }}
          headers={dippingData.map((d) => d.label)}
          data={dippingData}
          singleRow
        />
      </Grid>
    </Grid>
  );
};

const ProductionChart = ({ chartData }) => {
  const safeChartData =
    Array.isArray(chartData) && chartData.length > 0 ? chartData : [];

  if (safeChartData.length === 0) {
    return <div>No production data available for the selected period.</div>;
  }

  const categories = safeChartData.map((item) =>
    dayjs(item.date).format("DD/MM/YYYY")
  );
  const completedBatchData = safeChartData.map((item) => item.completed);
  const inProgressBatchData = safeChartData.map((item) => item.inprogress);
  const aboartBatchData = safeChartData.map((item) => item.aborted);

  const chartOptions = {
    chart: {
      type: "area",
      backgroundColor: "#ffffff",
      borderRadius: 12,
      height: 330,
      zoomType: "x",
    },
    title: { text: null },
    xAxis: {
      categories,
      labels: { style: { color: "#666" } },
      lineColor: "#e0e0e0",
      tickColor: "#e0e0e0",
    },
    yAxis: {
      title: { text: "Number of Batches" },
      labels: { style: { color: "#666" } },
      gridLineColor: "#e0e0e0",
      gridLineDashStyle: "dot",
    },
    tooltip: {
      shared: true,
      pointFormat:
        '<span style="color:{point.color}">\u25CF</span> {series.name}: {point.y}<br/>',
      headerFormat: "<b>{point.key}</b><br/>",
    },
    exporting: {
      enabled: false,
    },
    credits: { enabled: false },
    legend: {
      enabled: true,
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        fontSize: "12px",
      },
    },
    scrollbar: { enabled: true },
    plotOptions: {
      area: {
        stacking: null,
        lineWidth: 2,
        marker: {
          enabled: safeChartData.length === 1,
          radius: 4,
          symbol: "circle",
        },
      },
      series: {
        marker: {
          enabled: safeChartData.length === 1,
        },
      },
    },
    series: [
      {
        name: "Abort Batches",
        data: aboartBatchData,
        color: "red",
        fillColor: "rgba(255, 0, 0, 0.5)",
        marker: {
          fillColor: "red",
          lineColor: "red",
        },
      },
      {
        name: "In Progress Batches",
        data: inProgressBatchData,
        color: "#fd7e14",
        fillColor: "rgba(253, 126, 20, 0.5)",
        marker: {
          fillColor: "#fd7e14",
          lineColor: "#fd7e14",
        },
      },
      {
        name: "Completed Batches",
        data: completedBatchData,
        color: "#28a745",
        fillColor: "rgba(40, 167, 69, 0.5)",
        marker: {
          fillColor: "#28a745",
          lineColor: "#28a745",
        },
      },
    ],
  };

  return <HighchartsReact highcharts={Highcharts} options={chartOptions} />;
};

export default function App({ mergedArray, startDateData, endDateData }) {
  const [showChart, setShowChart] = useState(false);
  const [dayWiseBatchStatusChartData, setDayWiseBatchStatusChartData] =
    useState([]);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await getDayWiseBatchStatusCount(
          startDateData,
          endDateData
        );
        setDayWiseBatchStatusChartData(response.data.payload || []);
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };
    if (!isNaN(new Date(startDateData)) && !isNaN(new Date(endDateData))) {
      fetchChartData();
      const interval = setInterval(() => {
        fetchChartData(); // Call API every 15 min
      }, 15 * 60 * 1000); // 15 minutes
  
      return () => clearInterval(interval);
    }
  }, [startDateData, endDateData]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ padding: "12px 16px" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography variant="h5" sx={{ fontSize: "18px", display: "flex" }}>
            {showChart ? "Daily Production Pattern" : "Process Parameters"}
            {!showChart && (
            <Typography
            variant="h5"
            sx={{
              fontSize: "18px",
              color: "green",
              marginLeft: "8px",
              fontFamily: "'Orbitron', sans-serif",
              animation: "blink 1s infinite",
              "@keyframes blink": {
                "0%": { opacity: 1 },
                "50%": { opacity: 0 },
                "100%": { opacity: 1 },
              },
            }}
          >
            Live
          </Typography>

            )}
          </Typography>
          <IconButton
            onClick={() => setShowChart((prev) => !prev)}
            sx={{ backgroundColor: "#fff", border: "1px solid #e0e0e0" }}
          >
            {showChart ? (
              <ArrowBackIosNewIcon fontSize="small" />
            ) : (
              <ArrowForwardIosIcon fontSize="small" />
            )}
          </IconButton>
        </Box>

        {showChart ? (
          <ProductionChart chartData={dayWiseBatchStatusChartData} />
        ) : (
          <ProcessParametersComponent mergedArray={mergedArray} />
        )}
      </Box>
    </ThemeProvider>
  );
}